
import React, { useState, useEffect } from 'react';
import { db } from '../services/firebase';
import { collection, onSnapshot, addDoc, deleteDoc, doc, query, where } from 'firebase/firestore';
import { UserRole } from '../types';

interface ClassManagementProps {
    user: any;
    onClassClick?: (classId: string) => void;
}

const ClassManagement: React.FC<ClassManagementProps> = ({ user, onClassClick }) => {
    const [classes, setClasses] = useState<any[]>([]);
    const [students, setStudents] = useState<any[]>([]);
    const [name, setName] = useState('');
    const [selectedClassForStudent, setSelectedClassForStudent] = useState<any>(null);

    // Robust Natural Sorting: Descending (12th, 11th, 10th...)
    const naturalSortClasses = (classList: any[]) => {
        return [...classList].sort((a, b) => {
            const extractNum = (s: string) => {
                const match = s.match(/\d+/);
                return match ? parseInt(match[0], 10) : 0;
            };
            const numA = extractNum(a.name);
            const numB = extractNum(b.name);
            
            if (numA !== numB) return numB - numA; // Higher classes first
            return a.name.localeCompare(b.name);
        });
    };

    useEffect(() => {
        let classQuery;
        if (user.role === UserRole.STUDENT) {
            if (user.classId) {
                classQuery = query(collection(db, 'classes'), where('__name__', '==', user.classId));
            } else {
                setClasses([]);
            }
        } else {
            classQuery = collection(db, 'classes');
        }

        let unsubscribeClasses = () => {};
        if (classQuery) {
            unsubscribeClasses = onSnapshot(classQuery, (snapshot) => {
                const rawClasses = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                setClasses(naturalSortClasses(rawClasses));
            });
        }

        let unsubscribeStudents = () => {};
        if (user.role !== UserRole.STUDENT) {
            unsubscribeStudents = onSnapshot(query(collection(db, 'users'), where('role', '==', UserRole.STUDENT)), (snapshot) => {
                setStudents(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            });
        }

        return () => {
            unsubscribeClasses();
            unsubscribeStudents();
        };
    }, [user]);

    const generateClassCode = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        let result = '';
        for (let i = 0; i < 4; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    };

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        if (user.role === UserRole.STUDENT) return;
        await addDoc(collection(db, 'classes'), { 
            name, 
            code: generateClassCode(),
            createdBy: user.uid,
            createdAt: Date.now() 
        });
        setName('');
    };

    const getStudentCount = (classId: string) => {
        return students.filter(s => s.classId === classId).length;
    };

    if (user.role === UserRole.STUDENT && selectedClassForStudent) {
        return (
            <div className="space-y-8 animate-fadeIn">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => setSelectedClassForStudent(null)}
                        className="w-10 h-10 rounded-xl bg-white dark:bg-dark-card shadow-sm flex items-center justify-center text-gray-500 hover:text-purple-600 transition-all"
                    >
                        <i className="fas fa-chevron-left"></i>
                    </button>
                    <div>
                        <h2 className="text-3xl font-black dark:text-white tracking-tight">{selectedClassForStudent.name}</h2>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">Classroom Command Center</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <button 
                        onClick={() => onClassClick?.('subjects')}
                        className="p-8 bg-white dark:bg-dark-card rounded-[2.5rem] border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all group text-left"
                    >
                        <div className="w-14 h-14 bg-blue-600 text-white rounded-2xl flex items-center justify-center shadow-lg mb-6 group-hover:rotate-6 transition-transform">
                            <i className="fas fa-book-open text-xl"></i>
                        </div>
                        <h3 className="text-xl font-black dark:text-white mb-2">Academic Subjects</h3>
                        <p className="text-xs text-gray-400 font-medium">Access curriculum, notes and resources</p>
                    </button>

                    <button 
                        onClick={() => onClassClick?.('my-attendance')}
                        className="p-8 bg-white dark:bg-dark-card rounded-[2.5rem] border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all group text-left"
                    >
                        <div className="w-14 h-14 bg-green-600 text-white rounded-2xl flex items-center justify-center shadow-lg mb-6 group-hover:rotate-6 transition-transform">
                            <i className="fas fa-calendar-check text-xl"></i>
                        </div>
                        <h3 className="text-xl font-black dark:text-white mb-2">Attendance Register</h3>
                        <p className="text-xs text-gray-400 font-medium">Track your presence and absence records</p>
                    </button>

                    <button 
                        onClick={() => onClassClick?.('my-scores')}
                        className="p-8 bg-white dark:bg-dark-card rounded-[2.5rem] border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all group text-left"
                    >
                        <div className="w-14 h-14 bg-orange-600 text-white rounded-2xl flex items-center justify-center shadow-lg mb-6 group-hover:rotate-6 transition-transform">
                            <i className="fas fa-star text-xl"></i>
                        </div>
                        <h3 className="text-xl font-black dark:text-white mb-2">Performance & Marks</h3>
                        <p className="text-xs text-gray-400 font-medium">View exam results and academic progress</p>
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fadeIn">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h2 className="text-3xl font-black dark:text-white tracking-tight">Academic Rooms</h2>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">Management Console • Session 2026-27</p>
                </div>
                {user.role !== UserRole.STUDENT && (
                    <div className="flex items-center gap-2 bg-white dark:bg-dark-card p-2 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800">
                        <span className="text-[10px] font-black text-purple-600 uppercase tracking-widest px-4">Rooms Active: {classes.length}</span>
                    </div>
                )}
            </div>
            
            {user.role !== UserRole.STUDENT && (
                <form onSubmit={handleAdd} className="bg-white dark:bg-dark-card p-4 lg:p-6 rounded-[2.5rem] shadow-xl border border-gray-100 dark:border-gray-800 flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative group">
                        <i className="fas fa-school absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-purple-500 transition-colors"></i>
                        <input 
                            required 
                            placeholder="Room Designation (e.g. Class 12th)" 
                            className="w-full bg-gray-50 dark:bg-gray-800/50 pl-14 pr-6 py-5 rounded-[1.5rem] dark:text-white outline-none border border-transparent focus:border-purple-500 transition-all font-bold" 
                            value={name} 
                            onChange={e => setName(e.target.value)} 
                        />
                    </div>
                    <button type="submit" className="px-10 py-5 bg-purple-600 text-white font-black rounded-[1.5rem] hover:bg-purple-700 shadow-2xl shadow-purple-200 dark:shadow-none transition-all active:scale-95 flex items-center justify-center gap-3">
                        <i className="fas fa-plus-circle"></i> Create Room
                    </button>
                </form>
            )}
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {classes.map(c => (
                    <div 
                        key={c.id} 
                        onClick={() => user.role === UserRole.STUDENT && setSelectedClassForStudent(c)}
                        className={`bg-white dark:bg-dark-card p-8 rounded-[3rem] border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all relative overflow-hidden group ${user.role === UserRole.STUDENT ? 'cursor-pointer' : ''}`}
                    >
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/10 to-transparent -mr-12 -mt-12 rounded-full group-hover:scale-125 transition-transform"></div>
                        
                        <div className="relative z-10">
                            <div className="w-14 h-14 bg-purple-600 text-white rounded-2xl flex items-center justify-center shadow-xl mb-6 group-hover:rotate-6 transition-transform">
                                <i className="fas fa-door-open text-xl"></i>
                            </div>
                            
                            <h3 className="text-2xl font-black dark:text-white mb-2 leading-tight tracking-tight">{c.name}</h3>
                            <div className="flex items-center justify-between mb-8">
                                <div className="flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Active Section</span>
                                </div>
                                {user.role !== UserRole.STUDENT && (
                                    <div className="bg-purple-100 dark:bg-purple-900/30 px-3 py-1 rounded-lg border border-purple-200 dark:border-purple-800/50">
                                        <p className="text-[9px] font-black text-purple-600 uppercase tracking-tighter">Code: <span className="text-sm font-black ml-1">{c.code}</span></p>
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-2xl text-center">
                                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-tighter mb-1">Enrolled</p>
                                    <p className="text-xl font-black text-purple-600">{getStudentCount(c.id)}</p>
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-2xl text-center">
                                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-tighter mb-1">Pass Rate</p>
                                    <p className="text-xl font-black text-indigo-500">92%</p>
                                </div>
                            </div>

                            <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-50 dark:border-gray-800">
                                {user.role === UserRole.ADMIN ? (
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); if(confirm('Dissolve this room?')) deleteDoc(doc(db, 'classes', c.id)); }} 
                                        className="w-full py-3 text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all"
                                    >
                                        <i className="fas fa-trash-alt mr-2"></i> Delete Room
                                    </button>
                                ) : (
                                    <>
                                        <span className="text-[10px] font-black text-purple-600 uppercase tracking-widest">Academic Room</span>
                                        <i className="fas fa-chevron-right text-xs text-purple-600 group-hover:translate-x-1 transition-transform"></i>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            
            {classes.length === 0 && (
                <div className="text-center py-40 bg-white dark:bg-dark-card rounded-[4rem] border-2 border-dashed border-gray-100 dark:border-gray-800 shadow-inner">
                    <div className="w-24 h-24 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner">
                        <i className={`fas ${user.role === UserRole.STUDENT ? 'fa-user-slash' : 'fa-school-circle-exclamation'} text-4xl text-gray-200`}></i>
                    </div>
                    <h3 className="text-2xl font-black dark:text-white mb-3 tracking-tight">
                        {user.role === UserRole.STUDENT ? 'No Class Assigned' : 'Inventory Empty'}
                    </h3>
                    <p className="text-gray-400 text-sm max-w-sm mx-auto px-4 font-medium">
                        {user.role === UserRole.STUDENT 
                            ? "You aren't assigned to any classroom yet. Please visit the admin office for room allocation." 
                            : "Start by creating academic rooms to begin assigning subjects and tracking student progress."}
                    </p>
                </div>
            )}
        </div>
    );
};

export default ClassManagement;
